// A file as a row of a list (RFC-054): its kind, its name opening its page,
// under it whose it is when not mine and what it was handed in for or its
// description; then, each in a column of its own as a template's row has
// them, who sees it or how it reached me, its latest version and size, and
// when it last changed; what is done with it behind its ⋯. /files
// (byj-1wv.51.17), a class's files (ClassFiles) and a person's files on the
// Admin page (FileStorage) alike.
import type { ReactNode } from "react";
import type { FilePreview, ShareFile } from "../api";
import { t, when, whenShort, phrases } from "../i18n";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";
import { fileBytes } from "../files";

const icon = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

/** A file's sign in its row's box by what its latest version is, as the relay found it; one with nothing uploaded a dashed sheet. */
const fileIcon: Record<FilePreview | "none", ReactNode> = {
  folder: icon(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />),
  image: icon(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M21 16l-5-5-8 9" />
    </>,
  ),
  pdf: icon(
    <>
      <path d="M7 3h8l4 4v14H7z" />
      <path d="M15 3v4h4M10 13h6M10 17h4" />
    </>,
  ),
  text: icon(
    <>
      <path d="M7 3h8l4 4v14H7z" />
      <path d="M15 3v4h4M10 11h6M10 14h6M10 17h3" />
    </>,
  ),
  "": icon(
    <>
      <path d="M7 3h8l4 4v14H7z" />
      <path d="M15 3v4h4" />
    </>,
  ),
  none: icon(<path d="M7 3h8l4 4v14H7z" strokeDasharray="3 2" />),
};

export function FileRow({ f, menu, owner = true }: {
  f: ShareFile;
  menu: MenuItem[];
  /** whose it is, said when not mine; false where every row is one person's */
  owner?: boolean;
}) {
  const kind: FilePreview | "none" = f.kind === "folder" ? "folder" : f.latest ? f.preview : "none";
  const sub = [owner && !f.mine ? t("files:fileBy", { owner: f.owner }) : "", f.hand_in ? `${f.hand_in.name} · ${f.hand_in.title}` : f.description].filter(Boolean);
  // who sees it, as the relay says: mine, how many besides me (only /files' own
  // list counts them); a hand-in, until when it is kept; another's, how it reached me
  const seen =
    f.hand_in && f.expires_at
      ? t("files:fileKeptShort", { w: whenShort(f.expires_at) })
      : f.mine && f.viewers !== undefined && f.classes !== undefined
        ? phrases.fileSeenBy(f.viewers, f.classes)
        : null;
  return (
    <li className={`share-row file-row ${f.latest ? "is-live" : "is-ended"}`} data-testid={`file-row-${f.id}`}>
      <span className="row-lead" />
      <span className={`kind-box file-kind-${kind}`} aria-hidden="true">
        {fileIcon[kind]}
      </span>
      <div className="row-main">
        <Link to={`/f/${encodeURIComponent(f.id)}`} className="row-title file-name-strong">
          {f.name}
        </Link>
        {f.kind === "folder" && <span className="sr-only">{t("files:fileFolder")}</span>}
        {sub.length > 0 && (
          <div className="row-sub" title={sub.join(" · ")}>
            {sub.map((s, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      {/* each in a column of its own, so the rows line up; narrow, an empty one takes no room */}
      <div className="row-meta">
        <span className="row-via" title={f.expires_at ? t("files:fileKeptUntil", { w: when(f.expires_at) }) : undefined}>
          {seen ?? (!f.mine && f.via && <span className="chip">{phrases.templateVia(f.via)}</span>)}
        </span>
        <span className="row-version">
          <span className={`chip${f.latest ? "" : " chip-warn"}`}>{f.latest ? t("files:fileVersion", { n: f.latest }) : t("files:fileNoVersion")}</span>
          {f.latest > 0 && <span>{fileBytes(f.bytes)}</span>}
        </span>
        <time className="row-time" dateTime={f.updated_at} title={t("files:fileUpdated", { w: when(f.updated_at) })}>
          {whenShort(f.updated_at)}
        </time>
      </div>
      <RowMenu label={t("shares:moreFor", { title: f.name })} items={menu} />
    </li>
  );
}
